// FieldVisibility — when a field should be rendered.
// Per LLM_NATIVE_ARCHITECTURE.md §2.2.
//
// Why schema-level visibility (not view-level if-else):
//   View-level if-else for "should this AI output be visible right now?"
//   scatters across views. Adding a new view risks omitting the if-else and
//   leaking pre-quiz-completion mcq.explanation or pre-submission
//   essay.modelAnswer (see Session 19V mock-exam-view bug history).
//
// Schema-level: declare visibility once. Every view consumes via the
// `<Field gate={...}>` wrapper (packages/web). Forgetting the gate is
// impossible because there is no path to the value that bypasses it.
//
// Modes:
//   always              — show whenever the parent is rendered (most fields)
//   after_event         — show after a UI event (e.g. 'user_answered',
//                         'session_finished', 'submitted')
//   after_async         — show when an async produced field becomes ready
//                         (e.g. essay.aiScore !== null)
//   never_in_runtime    — internal metadata (only ever exported / inspected)
import { z } from 'zod';
export const FieldVisibilitySchema = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('always') }),
    z.object({ mode: z.literal('after_event'), event: z.string() }),
    z.object({ mode: z.literal('after_async'), readyWhen: z.string() }),
    z.object({ mode: z.literal('never_in_runtime') }),
]);
/** Convenience constructors (avoid the verbose object literal at call sites). */
export const visibility = {
    always: { mode: 'always' },
    afterEvent: (event) => ({ mode: 'after_event', event }),
    afterAsync: (readyWhen) => ({ mode: 'after_async', readyWhen }),
    neverInRuntime: { mode: 'never_in_runtime' },
};
/**
 * Evaluate gate against a runtime context. Used by view-side `<Field>` /
 * `<RevealAfter>` wrappers to decide whether to render their children.
 *
 * Caller passes:
 *   - eventsFired: set of events that have happened in this view session
 *     (e.g. {'user_answered'} once user submits a quiz answer)
 *   - asyncReady:  set of `readyWhen` keys that are currently true
 *     (e.g. {'essay.aiScore_ready'} when aiScore !== null)
 */
export function isVisible(gate, ctx = {}) {
    switch (gate.mode) {
        case 'always': return true;
        case 'never_in_runtime': return false;
        case 'after_event': return ctx.eventsFired?.has(gate.event) ?? false;
        case 'after_async': return ctx.asyncReady?.has(gate.readyWhen) ?? false;
    }
}
//# sourceMappingURL=field-visibility.js.map